import type {
  IInternalFormSchema,
  IInternalSchemaField
} from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { InternalFieldDisplay } from '../../components/field-controls';
import { flatInternalPayload } from '../../internal-field-values';
import { refusalMessage } from '../../refusal';
import { headlineSuffixOf } from '../person-hub/research-headline';

/**
 * The staff fields a work item is read with (SR-222, F9's Task 8; CMP-09): a survey queue's
 * column picker offers its survey's staff form, and an item's pane says the participant's
 * Eligibility — the Quality rating and the Travel that Remove writes.
 *
 * **What a queue can show is the survey's schema** (`internalFieldsApi.schema`, F2's one read per
 * survey per page load), less what a row cannot carry: a multi-select, which survey-api leaves out
 * of `internal` (it has no flat reading), and a managed field, whose value the hub derives in
 * memory while the list reads only stored values — a column of it would say "—" for a value the
 * hub shows. An admin-only field is absent from a staff caller's schema already.
 *
 * **Every offered field is asked for**, shown or not (`fields=`, one code each). Rejected: asking
 * only the shown ones, which would put the picker's state into the query and read the page again
 * on every box ticked.
 *
 * **Quality rating and Travel are shown by default**, found by survey-api's suffix rule
 * (`headlineSuffixOf`, F2's mirror): they are the two fields a Remove writes, so they are what
 * the queue is worked by. Approval status, the third headline, is the hub's and a box away.
 */

/** Why My tasks offers no staff field: a field is a survey's, and its rows are several
 *  surveys'. */
export const STAFF_FIELDS_NOTE = 'Choose a queue to add its staff fields.';

/** All's note: it offers the two Remove writes alone, each row's of its own survey. */
export const ALL_STAFF_FIELDS_NOTE = 'Choose a queue to add its other staff fields.';

/**
 * A staff field All offers over every queue (SR-410): survey-api's stem code, `*.` and the field's
 * name, fills each row's `internal` with its own survey's field of that name (`fa.travel` on an FA
 * row, `teams.travel` on a TEAMS one). `bySurvey` is each queue's field, read from its schema, so a
 * value is drawn by its own field's type and options; a queue whose schema could not be read draws
 * the value as sent.
 */
export interface IStemColumn {
  code: string;
  name: string;
  bySurvey: ReadonlyMap<string, IInternalSchemaField>;
}

/** The two Remove writes, as All asks for them — Quality Rating first, as a queue shows them. */
const STEMS = [
  { suffix: 'quality_rating', name: 'Quality Rating' },
  { suffix: 'travel', name: 'Travel' }
] as const;

/** The stem codes All asks its rows for. */
export const STEM_CODES: readonly string[] = STEMS.map((stem) => `*.${stem.suffix}`);

/** All's two staff columns over `surveys`' schemas — each read once a page load; one that fails
 *  leaves its queue's values drawn as sent. */
export async function readStemColumns(surveys: readonly string[]): Promise<IStemColumn[]> {
  const schemas = await Promise.all(
    surveys.map((survey) =>
      internalFieldsApi.schema(survey).then(
        (schema) => [survey, staffColumnFields(schema)] as const,
        () => [survey, []] as const
      )
    )
  );
  return STEMS.map(({ suffix, name }) => ({
    code: `*.${suffix}`,
    name,
    bySurvey: new Map(
      schemas.flatMap(([survey, fields]) => {
        const field = fields.find((one) => headlineSuffixOf(one.code) === suffix);
        return field ? [[survey, field] as const] : [];
      })
    )
  }));
}

/** The fields a queue's rows can carry, in the form's order. */
export function staffColumnFields(schema: IInternalFormSchema): IInternalSchemaField[] {
  return schema.groups
    .flatMap((group) => group.fields)
    .filter((field) => field.type !== 'multi_select' && field.managedBy === null);
}

/** A queue's staff fields, or what stopped them being read — the queue still draws without them. */
export interface IStaffColumns {
  fields: IInternalSchemaField[];
  error: Nullable<string>;
}

export async function readStaffColumns(surveyCode: string): Promise<IStaffColumns> {
  try {
    return { fields: staffColumnFields(await internalFieldsApi.schema(surveyCode)), error: null };
  } catch (failure) {
    return { fields: [], error: refusalMessage(failure, 'Could not read the staff fields') };
  }
}

/** The two fields a Remove writes — a queue's default staff columns, and the pane's
 *  Eligibility, Quality rating first. */
export function removeWrites(fields: readonly IInternalSchemaField[]): IInternalSchemaField[] {
  return (['quality_rating', 'travel'] as const).flatMap((suffix) =>
    fields.filter((field) => headlineSuffixOf(field.code) === suffix)
  );
}

/** One staff value as a row carries it — `text` absent where the participant has none; drawn as
 *  sent without its `field`. */
export function StaffValue({
  text,
  field
}: {
  text: string | undefined;
  field: IInternalSchemaField | undefined;
}) {
  if (text === undefined) return <span className="text-muted-foreground">—</span>;
  if (field === undefined) return <span className="wrap-anywhere">{text}</span>;
  const payload = flatInternalPayload(text, field.type);
  return payload ? (
    <InternalFieldDisplay payload={payload} options={field.options} />
  ) : (
    <span className="wrap-anywhere">{text}</span>
  );
}
